import { observable, action, makeObservable, computed, runInAction } from "mobx";
import { PROCESS, TICKET_STATE, Ticket } from "src/stores/TicketStore";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { GetRealestateNameById, GetRealestateNameByIdVariables } from "src/api/generated/GetRealestateNameById";
import { GetUnitNameById, GetUnitNameByIdVariables } from "src/api/generated/GetUnitNameById";
import { GET_REALESTATENAME_BY_ID } from "src/api/realestate";
import { GET_UNITNAME_BY_ID } from "src/api/realestateUnits";
import { SEARCH_TICKETS } from "src/api/ticket";
import { SearchTickets, SearchTicketsVariables } from "src/api/generated/SearchTickets";
import { parseSearchTicketIncidents } from "src/types/searchTickets";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { USER_SETTING } from "src/stores/UserSettingsStore";

export enum SORT {
    DATE_NEWEST = "dateNewest",
    DATE_OLDEST = "dateOldest"
}

export enum SORT_FIELD {
    NUMBER = "number",
    DATE = "date",
    TITLE = "title",
    REALESTATE = "realestate",
    INCIDENT_PERSONS = "incidentPersons",
    ASSIGNED = "assigned",
    ORDERLIST = "orderlist",
    STATUS = "status",
    NEW_MESSAGE = "newMessage"
}

export enum SORT_DIRECTION {
    ASC = "asc",
    DESC = "desc"
}

interface IKanbanBoard {
    [TICKET_STATE.NEW]: number[];
    [TICKET_STATE.ASSIGN_INCIDENT]: number[];
    [TICKET_STATE.ASSIGNED]: number[];
    [TICKET_STATE.COMISSIONED]: number[];
    [TICKET_STATE.COMPLETED]: number[];
    [TICKET_STATE.ARCHIVED]: number[];
}
export type FilterStateType = TICKET_STATE | "All" | "AllOpen";

export interface TicketStateCounts {
    new: number;
    assign_incident: number;
    assigned: number;
    comissioned: number;
    completed: number;
    archived: number;
}

export const EMPTY_TICKET_STATE_COUNTS: TicketStateCounts = {
    new: 0,
    assign_incident: 0,
    assigned: 0,
    comissioned: 0,
    completed: 0,
    archived: 0
};

export interface FilterSelection {
    label: string;
    id: FilterStateType;
}
export interface IFilter {
    state: FilterStateType;
    sort: SORT;
    sortField?: SORT_FIELD;
    sortDirection?: SORT_DIRECTION;
    query: string;
    types: PROCESS[];
    showOwn: boolean;
    showMyRealestates: boolean;
    showRealestate: boolean;
    showUnit: boolean;
    showNewMessages: boolean;
}
export interface SearchTag {
    label: string;
    id: string;
    active: boolean;
    type?: PROCESS;
}
export const hasNewMessagesInState = (counts: TicketStateCounts, state: FilterStateType): boolean => {
    switch (state) {
        case TICKET_STATE.NEW:
            return counts.new + counts.assign_incident > 0;
        case TICKET_STATE.ASSIGNED:
            return counts.assigned > 0;
        case TICKET_STATE.COMISSIONED:
            return counts.comissioned > 0;
        case TICKET_STATE.COMPLETED:
            return counts.completed > 0;
        case TICKET_STATE.ARCHIVED:
            return counts.archived > 0;
        case TICKET_STATE.ADDITIONAL:
            return counts.completed + counts.archived > 0;
        case "AllOpen":
            return counts.new + counts.assign_incident + counts.assigned + counts.comissioned > 0;
        case "All":
            return Object.values(counts).some((count) => count > 0);
        default:
            return false;
    }
};

export const getTicketStateWithId = (id: number): TICKET_STATE => {
    switch (id) {
        case 0:
            return TICKET_STATE.NEW;
        case 5:
            return TICKET_STATE.ASSIGN_INCIDENT;
        case 10:
            return TICKET_STATE.ASSIGNED;
        case 20:
            return TICKET_STATE.COMISSIONED;
        case 90:
            return TICKET_STATE.COMPLETED;
        case 99:
            return TICKET_STATE.ARCHIVED;
        default:
            return TICKET_STATE.NEW;
    }
};

export class TicketListStore {
    rootStore: RootStore;
    realestateId: string = "";
    unitId: string = "";

    ticketFilter: IFilter = {
        state: TICKET_STATE.NEW,
        sort: SORT.DATE_NEWEST,
        sortField: SORT_FIELD.NUMBER,
        sortDirection: SORT_DIRECTION.DESC,
        query: "",
        types: [],
        showOwn: true,
        showMyRealestates: false,
        showRealestate: this.realestateId.length > 0,
        showUnit: this.unitId.length > 0,
        showNewMessages: false
    };

    searchTags: SearchTag[] = [
        {
            label: i18next.t("screens.tickets.list.show_own"),
            id: "SHOW_OWN",
            active: this.ticketFilter.showOwn
        },
        { label: i18next.t("easyticket.workflow_types.1"), id: "1", active: false, type: PROCESS.COMMON_REQUEST },
        { label: i18next.t("easyticket.workflow_types.2"), id: "2", active: false, type: PROCESS.DAMAGE_NOTIFICATION },
        { label: i18next.t("easyticket.workflow_types.3"), id: "3", active: false, type: PROCESS.NAMESIGN },
        { label: i18next.t("easyticket.workflow_types.4"), id: "4", active: false, type: PROCESS.DOCUMENT_QUESTION },
        { label: i18next.t("easyticket.workflow_types.5"), id: "5", active: false, type: PROCESS.KEY_ORDER },
        { label: i18next.t("screens.tickets.list.my_realestates"), id: "SHOW_MYREALESTATE", active: false },
        { label: i18next.t("screens.tickets.list.new_message"), id: "SHOW_NEW_MESSAGE", active: false }
    ];

    kanbanBoard: IKanbanBoard = {
        [TICKET_STATE.NEW]: [],
        [TICKET_STATE.ASSIGN_INCIDENT]: [],
        [TICKET_STATE.ASSIGNED]: [],
        [TICKET_STATE.COMISSIONED]: [],
        [TICKET_STATE.COMPLETED]: [],
        [TICKET_STATE.ARCHIVED]: []
    };

    isTabAdditionalClicked: boolean = false;

    loadTicketsRequestId: number = 0;

    // Search and filter properties for server-side queries
    searchQuery: string = "";
    stateFilter: string = "{0, 5}"; // Default to NEW and ASSIGN_INCIDENT states
    sortField: string = SORT_FIELD.NUMBER;
    sortDirection: string = "desc";
    pageLimit: number = 20;
    pageOffset: number = 0;
    workflowTypes: string | null = null;
    assigneduserIds: string | null = null;
    onlyNewMessages: boolean = false;
    filterRealestatesByUserIds: string | null = null;
    incidentpersonIds: string | null = null;
    contractorIds: string | null = null;
    startDate: string | null = null;
    endDate: string | null = null;
    totalTicketCount: number = 0;
    ticketStateCounts: TicketStateCounts = { ...EMPTY_TICKET_STATE_COUNTS };
    newMessageStateCounts: TicketStateCounts = { ...EMPTY_TICKET_STATE_COUNTS };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        // Load saved sort preferences
        this.initSortPreferences();

        makeObservable(this, {
            kanbanBoard: observable,
            ticketFilter: observable,
            searchTags: observable,
            realestateId: observable,
            unitId: observable,
            isTabAdditionalClicked: observable,

            // SearchTickets properties
            searchQuery: observable,
            stateFilter: observable,
            sortField: observable,
            sortDirection: observable,
            pageLimit: observable,
            pageOffset: observable,
            workflowTypes: observable,
            assigneduserIds: observable,
            onlyNewMessages: observable,
            filterRealestatesByUserIds: observable,
            incidentpersonIds: observable,
            contractorIds: observable,
            startDate: observable,
            endDate: observable,
            totalTicketCount: observable,
            ticketStateCounts: observable,
            newMessageStateCounts: observable,

            resetKanbanBoard: action,
            setKanbanTicketState: action,
            setFilter: action,
            setTag: action,
            clearFilter: action,
            setRealestateId: action,
            setUnitId: action,
            getRealestateName: action,
            getUnitName: action,
            addSearchTag: action,
            setIsTabAdditionalClicked: action,
            setSortField: action,
            initSortPreferences: action,

            // SearchTickets actions
            setSearchQuery: action,
            setStateFilter: action,
            setSortDirection: action,
            setPageLimit: action,
            setPageOffset: action,
            setWorkflowTypes: action,
            setAssignedUserIds: action,
            setOnlyNewMessages: action,
            setFilterRealestatesByUserIds: action,
            setIncidentPersonIds: action,
            setContractorIds: action,
            setStartDate: action,
            setEndDate: action,
            loadTickets: action,

            filteredTickets: computed,
            ticketStats: computed,
            tickets: computed,
            currentTabTicketCount: computed,
            activeSearchTags: computed,
            inactiveSearchTags: computed
        });
    }

    // Initialize sort preferences by loading from UserSettingsStore or localStorage
    initSortPreferences = () => {
        // Load preferences asynchronously
        this.loadSortPreferences().catch((error) => {
            console.error("Error initializing sort preferences:", error);
        });
    };

    addSearchTag(label: string, id: string, active: boolean, type?: PROCESS): void {
        const newSearchTag: SearchTag = {
            label,
            id,
            active,
            type // Ensure type is PROCESS or undefined
        };

        // Push the new search tag to the searchTags array
        this.searchTags.push(newSearchTag);
    }

    get tickets() {
        return this.rootStore.ticketStore.tickets;
    }

    get activeSearchTags() {
        return this.searchTags.filter((tag) => tag.active);
    }

    get inactiveSearchTags() {
        return this.searchTags.filter((tag) => !tag.active);
    }

    get currentTabTicketCount() {
        // Sum the per-state counts for every state in the currently active filter
        // (e.g. NEW = "{0, 5}" -> new + assign_incident). Falls back to the grand
        // total only when no states can be parsed.
        const countByState: { [state: number]: number } = {
            0: this.ticketStateCounts.new,
            5: this.ticketStateCounts.assign_incident,
            10: this.ticketStateCounts.assigned,
            20: this.ticketStateCounts.comissioned,
            90: this.ticketStateCounts.completed,
            99: this.ticketStateCounts.archived
        };

        const states = this.stateFilter
            .replace(/[{}]/g, "")
            .split(",")
            .map((value) => parseInt(value.trim(), 10))
            .filter((value) => !isNaN(value));

        if (states.length === 0) {
            return this.totalTicketCount;
        }

        return states.reduce((sum, state) => sum + (countByState[state] ?? 0), 0);
    }

    getTicketByState = (state: TICKET_STATE) => {
        return this.tickets.filter((ticket) => ticket.state === state);
    };
    getTicketByNumber = (number: number) => {
        return this.tickets.find((ticket) => ticket.number === number);
    };

    setRealestateId = (realestateId: string) => {
        this.realestateId = realestateId;
        this.loadTickets();
    };

    setUnitId = (unitId: string) => {
        this.unitId = unitId;
    };

    setIsTabAdditionalClicked = (isTabAdditionalClicked: boolean) => {
        this.isTabAdditionalClicked = isTabAdditionalClicked;
    };

    get filteredTickets() {
        const filter = this.ticketFilter;
        return this.tickets
            .filter((ticket) => {
                if (filter.query === "") {
                    return true;
                }

                const query = filter.query.toLowerCase();

                // Check if the query matches any order.number, ticket,number or realestate.zip exactly
                const hasMatchingOrder = ticket.orders?.some(
                    (order) => order.number?.toString().toLowerCase() === query
                );
                const matchesTicketNumber = ticket.number?.toString().toLowerCase() === query;
                const matchesRealestateZip = ticket.realestate?.zip?.toString().toLowerCase() === query;

                // Check if query is numeric
                const isQueryNumeric = !isNaN(Number(query));
                const searchString = [
                    ticket.title ?? "",
                    ticket.incidentContactPerson?.name1 ?? "",
                    ticket.incidentContactPerson?.name2 ?? "",
                    ticket.realestate?.id ?? "",
                    ticket.realestate?.name ?? "",
                    ticket.realestate?.city ?? "",
                    ticket.appliance ?? "",
                    ticket.unit?.unitId ?? "",
                    ticket.assignedUser?.name1 ?? "",
                    ticket.assignedUser?.name2 ?? ""
                ]
                    .join("")
                    .toLowerCase();

                // If the query is numeric, match ticket.number, order.number, or realestate.zip
                if (isQueryNumeric) {
                    return matchesTicketNumber || hasMatchingOrder || matchesRealestateZip;
                }

                // For non-numeric queries, match against the search string
                return searchString.includes(query) || matchesTicketNumber || hasMatchingOrder || matchesRealestateZip;
            })

            .filter((ticket) => {
                if (filter.showOwn) {
                    //TODO: Filter realestatesuser as well
                    return (
                        !ticket.assignedUser?.id || ticket.assignedUser?.id === this.rootStore.authStore.user?.userid
                    );
                }
                return true;
            })
            .filter((ticket) => {
                if (filter.showRealestate) {
                    return ticket.realestate?.id === this.realestateId;
                }
                return true;
            })
            .filter((ticket) => {
                if (filter.showUnit) {
                    return ticket.unit?.unitId === this.unitId;
                }
                return true;
            })
            .filter((ticket) => {
                if (filter.types.length === 0) {
                    return true;
                }
                return filter.types.includes(ticket.workflow_type);
            })

            .sort((ticketA, ticketB) => {
                // Define comparison helpers
                const compareStrings = (valueA: string, valueB: string, sortDirection: number) =>
                    sortDirection * valueA.localeCompare(valueB);
                const compareNumbers = (valueA: number, valueB: number, sortDirection: number) =>
                    sortDirection * (valueA - valueB);

                // Default to date sorting if no field specified
                const sortFieldToUse = filter.sortField || SORT_FIELD.DATE;
                // Default to descending (newest first) if no direction specified
                const sortMultiplier = filter.sortDirection === SORT_DIRECTION.ASC ? 1 : -1;

                // Map fields to their values
                const getValue = (ticket: Ticket, fieldName: SORT_FIELD) => {
                    switch (fieldName) {
                        case SORT_FIELD.NUMBER:
                            return ticket.number ?? 0;
                        case SORT_FIELD.DATE:
                            return ticket.date ?? 0;
                        case SORT_FIELD.TITLE:
                            return ticket.title ?? "";
                        case SORT_FIELD.REALESTATE:
                            return ticket.realestate?.name ?? "";
                        case SORT_FIELD.INCIDENT_PERSONS:
                            return ticket.incidentContactPerson?.name1 ?? "";
                        case SORT_FIELD.ASSIGNED:
                            return ticket.assignedUser?.name1 ?? "";
                        case SORT_FIELD.ORDERLIST:
                            return ticket.orders?.length ?? 0;
                        case SORT_FIELD.STATUS:
                            return ticket.state ?? "";
                        default:
                            return 0;
                    }
                };

                const ticketAFieldValue = getValue(ticketA, sortFieldToUse);
                const ticketBFieldValue = getValue(ticketB, sortFieldToUse);

                // Compare based on data type
                if (typeof ticketAFieldValue === "string" && typeof ticketBFieldValue === "string") {
                    return compareStrings(ticketAFieldValue, ticketBFieldValue, sortMultiplier);
                } else {
                    return compareNumbers(Number(ticketAFieldValue), Number(ticketBFieldValue), sortMultiplier);
                }
            });
    }

    get ticketStats() {
        const allTickets = [...this.filteredTickets];
        const getLength = (ticketState: TICKET_STATE) => {
            return allTickets.filter((t) => t.state === ticketState)?.length ?? 0;
        };
        return {
            new: getLength(TICKET_STATE.NEW),
            assign_incident: getLength(TICKET_STATE.ASSIGN_INCIDENT),
            assigned: getLength(TICKET_STATE.ASSIGNED),
            comissioned: getLength(TICKET_STATE.COMISSIONED),
            completed: getLength(TICKET_STATE.COMPLETED),
            archived: getLength(TICKET_STATE.ARCHIVED),
            total: { total: this.tickets.length, filtered: allTickets.length }
        };
    }

    resetKanbanBoard = () => {
        this.kanbanBoard = {
            [TICKET_STATE.NEW]: [],
            [TICKET_STATE.ASSIGN_INCIDENT]: [],
            [TICKET_STATE.ASSIGNED]: [],
            [TICKET_STATE.COMISSIONED]: [],
            [TICKET_STATE.COMPLETED]: [],
            [TICKET_STATE.ARCHIVED]: []
        };

        this.filteredTickets.forEach((ticket) => {
            this.kanbanBoard[ticket.state].push(ticket.number || 0);
        });
    };

    setKanbanTicketState = (
        newState: TICKET_STATE,
        oldState: TICKET_STATE,
        ticketNumber: number,
        newIndex: number,
        oldIndex: number
    ) => {
        const oldColumn = this.kanbanBoard[oldState];
        const newColumn = this.kanbanBoard[newState];

        if (oldState === newState) {
            const newTicketArray = newColumn;
            newTicketArray.splice(oldIndex, 1);
            newTicketArray.splice(newIndex, 0, ticketNumber);
            this.kanbanBoard[newState] = newTicketArray;
            return;
        } else {
            const oldTicketsArray = oldColumn;
            oldTicketsArray.splice(oldIndex, 1);

            const newTicketsArray = newColumn;
            newTicketsArray.splice(newIndex, 0, ticketNumber);

            this.kanbanBoard[newState] = newTicketsArray;
            this.kanbanBoard[oldState] = oldTicketsArray;
        }
    };

    setFilter = (newFilter: IFilter) => {
        this.ticketFilter = newFilter;
        this.resetKanbanBoard();
    };

    clearFilter = () => {
        this.searchTags = [...this.searchTags].map((t) => {
            return { ...t, active: false };
        });

        // Preserve the current state (tab) while clearing other filters
        this.setFilter({
            state: this.ticketFilter.state,
            sort: SORT.DATE_NEWEST,
            query: "",
            types: [],
            showOwn: false,
            showMyRealestates: false,
            showRealestate: false,
            showUnit: false,
            showNewMessages: false
        });

        // Reset server-side filter parameters
        this.searchQuery = "";
        this.workflowTypes = null;
        this.assigneduserIds = null;
        this.onlyNewMessages = false;
        this.filterRealestatesByUserIds = null;
        this.realestateId = "";
        this.unitId = "";
    };

    setTag = (id: string, active: boolean) => {
        const newSearchTags: SearchTag[] = [...this.searchTags].map((t) => {
            if (t.id === id) {
                return { ...t, active: active };
            }
            return t;
        });

        const activeProcesses: PROCESS[] = [];

        newSearchTags.forEach((tag) => {
            if (tag.active && tag.type) {
                activeProcesses.push(tag.type);
            }
        });

        this.searchTags = newSearchTags;

        const showOwn = newSearchTags.find((st) => st.id === "SHOW_OWN")?.active ?? false;

        const userid = this.rootStore.authStore.user?.userid;
        this.assigneduserIds = showOwn && userid ? `{${userid}}` : null;

        const showNewMessages = newSearchTags.find((st) => st.id === "SHOW_NEW_MESSAGE")?.active ?? false;
        this.onlyNewMessages = showNewMessages;

        this.setFilter({
            ...this.ticketFilter,
            types: activeProcesses,
            showOwn,
            showMyRealestates: newSearchTags.find((st) => st.id === "SHOW_MYREALESTATE")?.active ?? false,
            showRealestate: newSearchTags.find((st) => st.id.startsWith("SHOW_REALESTATE:"))?.active ?? false,
            showUnit: newSearchTags.find((st) => st.id.startsWith("SHOW_UNIT:"))?.active ?? false,
            showNewMessages
        });
    };

    getRealestateName = async () => {
        const { data } = await apolloClientInstance.query<GetRealestateNameById, GetRealestateNameByIdVariables>({
            query: GET_REALESTATENAME_BY_ID,
            variables: {
                realestateid: this.realestateId
            }
        });

        return data.common_realestates.length > 0 ? data.common_realestates[0].name : "";
    };

    getUnitName = async () => {
        const { data } = await apolloClientInstance.query<GetUnitNameById, GetUnitNameByIdVariables>({
            query: GET_UNITNAME_BY_ID,
            variables: {
                unitid: this.unitId
            }
        });

        return data.common_units.length > 0 ? data.common_units[0].name : "";
    };

    loadSortPreferences = async () => {
        try {
            if (this.rootStore.authStore.user?.userid) {
                const userSettingValue = await this.rootStore.userSettingsStore.loadUserSetting(
                    USER_SETTING.TICKET_SORT_PREFERENCES
                );

                if (userSettingValue) {
                    const sortPrefs = JSON.parse(userSettingValue);
                    if (sortPrefs.sortField && sortPrefs.sortDirection) {
                        this.ticketFilter = {
                            ...this.ticketFilter,
                            sortField: sortPrefs.sortField as SORT_FIELD,
                            sortDirection: sortPrefs.sortDirection as SORT_DIRECTION
                        };
                        return;
                    }
                }
            }
        } catch (error) {
            console.error("Error loading sort preferences:", error);
        }
    };

    saveSortPreferences = () => {
        try {
            if (this.ticketFilter.sortField && this.ticketFilter.sortDirection) {
                const sortPrefs = {
                    sortField: this.ticketFilter.sortField,
                    sortDirection: this.ticketFilter.sortDirection
                };

                if (this.rootStore.authStore.user?.userid) {
                    this.rootStore.userSettingsStore.setUserSetting({
                        key: USER_SETTING.TICKET_SORT_PREFERENCES,
                        value: JSON.stringify(sortPrefs)
                    });
                }
            }
        } catch (error) {
            console.error("Error saving sort preferences:", error);
        }
    };

    // Method to set the sort field and direction
    setSortField = (field: SORT_FIELD, reload: boolean = true) => {
        // If already sorting by this field, toggle direction
        if (this.ticketFilter.sortField === field) {
            const newDirection =
                this.ticketFilter.sortDirection === SORT_DIRECTION.DESC ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC;

            this.ticketFilter = {
                ...this.ticketFilter,
                sortDirection: newDirection
            };

            // Update server-side properties
            this.sortDirection = newDirection === SORT_DIRECTION.ASC ? "asc" : "desc";
        } else {
            // Default to descending for new sort field
            this.ticketFilter = {
                ...this.ticketFilter,
                sortField: field,
                sortDirection: SORT_DIRECTION.DESC
            };

            // Update server-side properties
            this.sortField = field;
            this.sortDirection = "desc";
        }

        // Save the preferences
        this.saveSortPreferences();

        // Reload tickets with new sort
        if (reload) {
            this.loadTickets();
        }
    };

    // SearchTickets methods
    setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    setStateFilter = (filter: string) => {
        this.stateFilter = filter;
    };

    setSortDirection = (direction: string) => {
        this.sortDirection = direction;
    };

    setPageLimit = (limit: number) => {
        this.pageLimit = limit;
    };

    setPageOffset = (offset: number, reload: boolean = true) => {
        this.pageOffset = offset;
        if (reload) {
            this.loadTickets();
        }
    };

    setWorkflowTypes = (workflowTypes: string | null) => {
        this.workflowTypes = workflowTypes;
    };

    setAssignedUserIds = (assigneduserIds: string | null) => {
        this.assigneduserIds = assigneduserIds;
    };

    setOnlyNewMessages = (onlyNewMessages: boolean) => {
        this.onlyNewMessages = onlyNewMessages;
    };

    setFilterRealestatesByUserIds = (userIds: string | null) => {
        this.filterRealestatesByUserIds = userIds;
    };

    setIncidentPersonIds = (incidentpersonIds: string | null) => {
        this.incidentpersonIds = incidentpersonIds;
    };

    setContractorIds = (contractorIds: string | null) => {
        this.contractorIds = contractorIds;
    };

    setStartDate = (startDate: string | null) => {
        this.startDate = startDate;
    };

    setEndDate = (endDate: string | null) => {
        this.endDate = endDate;
    };

    loadTickets = async () => {
        const requestId = ++this.loadTicketsRequestId;

        const { data } = await apolloClientInstance.query<SearchTickets, SearchTicketsVariables>({
            query: SEARCH_TICKETS,
            fetchPolicy: "no-cache",
            variables: {
                input: {
                    lang: "de",
                    customer_id: this.rootStore.authStore.user?.customerid,
                    state_filters: this.stateFilter,
                    lim: this.pageLimit,
                    off: this.pageOffset,
                    search_term: this.searchQuery || null,
                    sort_field: this.sortField,
                    sort_direction: this.sortDirection,
                    workflow_types: this.workflowTypes,
                    unit_id: this.unitId || null,
                    assigneduser_ids: this.assigneduserIds,
                    realestate_ids: this.realestateId ? `{${this.realestateId}}` : null,
                    incidentperson_ids: this.incidentpersonIds,
                    contractor_ids: this.contractorIds,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    only_new_messages: this.onlyNewMessages,
                    filter_realestates_by_user_ids: this.filterRealestatesByUserIds
                }
            }
        });

        if (requestId !== this.loadTicketsRequestId) {
            return;
        }

        if (data && data.ticket_search_tickets) {
            const result = data.ticket_search_tickets;
            const parsedIncidents = parseSearchTicketIncidents(result.incidents);

            const newTickets: Ticket[] = parsedIncidents
                .map((ticket) => this.rootStore.ticketStore.getTicketPropertiesObjectFromIncidentQuery(ticket))
                .map((newTicketData) => new Ticket(this.rootStore, newTicketData));

            runInAction(() => {
                this.ticketStateCounts = {
                    new: result.new || 0,
                    assign_incident: result.assign_incident || 0,
                    assigned: result.assigned || 0,
                    comissioned: result.comissioned || 0,
                    completed: result.completed || 0,
                    archived: result.archived || 0
                };
                this.newMessageStateCounts = {
                    new: result.new_message_new || 0,
                    assign_incident: result.new_message_assign_incident || 0,
                    assigned: result.new_message_assigned || 0,
                    comissioned: result.new_message_comissioned || 0,
                    completed: result.new_message_completed || 0,
                    archived: result.new_message_archived || 0
                };
                this.totalTicketCount = result.total || 0;
                this.rootStore.ticketStore.setInitialLoadAllTicketsCompleted(true);
            });

            this.rootStore.ticketStore.setTickets(newTickets);
        } else {
            this.rootStore.ticketStore.setTickets([]);
            runInAction(() => {
                this.ticketStateCounts = { ...EMPTY_TICKET_STATE_COUNTS };
                this.newMessageStateCounts = { ...EMPTY_TICKET_STATE_COUNTS };
                this.totalTicketCount = 0;
                this.rootStore.ticketStore.setInitialLoadAllTicketsCompleted(true);
            });
        }
    };
}
